export interface RtsOption extends Partial<RtsOptionPartial> {
  media: HTMLVideoElement
}

interface RtsOptionPartial {
  url: string
  retryCount: number
  retryDelay: number
  loadTimeout: number
  fetchOptions: RequestInit
  stallInterval: number
  delayHint: number
  enableSei: boolean
  checkStatsErrorDelay: number
  enableNetworkQuality: boolean
  networkQuality: INetworkQualityOption
  enableAdaptiveJitterBuffer: boolean
  adaptiveJitterBuffer: IAdaptiveJitterBufferOption
  networkStrategy: INetworkStrategy
  backupConstruct: any
  backupURL: string
  preloadPromise: IPreloadPromise
  urlParams: Record<string, string>
}

export interface IPreloadPromise {
  url: string
  promise: Promise<{
    pc: RTCPeerConnection
    audioTransceiver: RTCRtpTransceiver
    videoTransceiver: RTCRtpTransceiver
    answerSdp: string
  }>
}

export interface INetworkQualityOption {
  networkQos: {
    [key: number]: {
      maxRtt: number
      maxLoss: number
      minRetransmittedRate?: number
    }
  }
  networkQoe: {
    audioDelay: number
    audioStallRadio: number
    videoDelay: number
    videoStallRadio: number
  }
}

export interface INetworkStrategy {
  [key: number]: {
    jitterBuffer: number | null
  }
}

export interface IAdaptiveJitterBufferOption {
  networkWindow: number
  windowMethod: 'min' | 'max' | 'mid'
  loopInterval: number
  maxAvSyncDelay: number
  maxJitterBufferDiff: number
  maxStep: number
}

export function getOption(options: RtsOption): RtsOption {
  const { adaptiveJitterBuffer = {}, ...opts } = options || {}
  return {
    retryCount: 0,
    retryDelay: 1000,
    loadTimeout: 5000,
    stallInterval: 400,
    delayHint: 0,
    enableSei: false,
    checkStatsErrorDelay: 5000,
    enableNetworkQuality: true,
    networkQuality: {
      networkQos: {
        1: {
          maxRtt: 250,
          maxLoss: 0.15
        },
        2: {
          maxRtt: 500,
          maxLoss: 0.3,
          minRetransmittedRate: 0.2
        },
        3: {
          maxRtt: 750,
          maxLoss: 0.45,
          minRetransmittedRate: 0.35
        },
        4: {
          maxRtt: 1000,
          maxLoss: 0.6,
          minRetransmittedRate: 0.5
        }
      },
      networkQoe: {
        audioDelay: 2000,
        audioStallRadio: 0.3,
        videoDelay: 2000,
        videoStallRadio: 0.6
      }
    },
    enableAdaptiveJitterBuffer: false,
    adaptiveJitterBuffer: {
      // 动态JB的调整窗口。比如设置为3，则表示在最近3个网络质量变化周期内，计算出一个新的JB值。
      networkWindow: 3,
      // 窗口取值方法。min: 取窗口内最小值，max: 取窗口内最大值，mid: 取窗口内平均值
      windowMethod: 'max',
      // 循环设置JB的间隔(ms)。即每隔500ms就会尝试设置一次JB值
      loopInterval: 500,
      // 能容忍的最大音视频同步延迟(ms)。即如果探测到音视频同步延迟超过250ms，不会调整JB
      maxAvSyncDelay: 250,
      // 最大目标jb和当前jb的差值(ms)。即如果目标jb和当前jb的差值超过200ms，不会调整JB
      maxJitterBufferDiff: 300,
      // 每次设置JB的最大步长(ms)。即每次调整JB的大小不会超过300ms
      maxStep: 300,
      ...adaptiveJitterBuffer
    },
    networkStrategy: {
      1: {
        jitterBuffer: null
      },
      // [2, 3)
      2: {
        jitterBuffer: 1.5
      },
      // [3, 4)
      3: {
        jitterBuffer: 2
      },
      // [4, 5)
      4: {
        jitterBuffer: 3
      },
      // 5
      5: {
        jitterBuffer: 3
      }
    },
    ...opts
  }
}
